import { ReactNode, useState } from 'react';
import { useMutation } from 'react-query';
import { api } from '../../../services/api';
import { Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import MoreMenu from '../../ui/MoreMenu';
import Pill from '../../ui/Pill';
import { ErrorNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass, money, toCents } from '../../../lib/programs';
import { EventMoney, GiftRequest, Step, dateText } from '../../../lib/eventMoney';
import type { SimplePanelProps } from '../adapt';
import { headRow, td, th } from '../logistics';
import { stack, useMarkPresent, usePresent } from '../dayOfTypes';
import { CountsOnly, MoneyField, budgetLeadKey, moneyError, useEventMoney, useMoneyRefresh } from './BudgetPanel';

/** Keys VendorsPanel and this panel mark while on the page, so empty vendors and gift cards fold into one table. */
export const vendorsKey = (eventId: string) => `money-vendors:${eventId}`;
export const giftCardsKey = (eventId: string) => `money-giftcards:${eventId}`;

export function GiftCardDialog({ eventId, data, onDone }: { eventId: string; data: EventMoney; onDone: () => void }) {
  const [form, setForm] = useState({ route: 'pcard', count: '', value: '', use: '', vendor: '', recipients: '' });
  const refresh = useMoneyRefresh(eventId);
  const save = useMutation(
    async () => {
      const value = toCents(form.value);
      if (Number.isNaN(value)) throw new Error('Value per card: enter an amount like 25.00');
      return api.post(`/events/${eventId}/money/gift-cards`, { ...form, count: Number(form.count), valueCents: value });
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title="Request gift cards"
      why="A PCard purchase needs Procurement’s approval first (count, value, total, vendor, use, recipients). A gift to an Elder or a research participant can go as a participant fee instead (capped), or as an honorarium."
      onClose={onDone}
      width={620}
      footer={
        <>
          <Button variant="primary" disabled={save.isLoading} onClick={() => save.mutate()}>
            Add the request
          </Button>
          <Button onClick={onDone}>Cancel</Button>
        </>
      }
    >
      {save.error ? moneyError(save.error) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoneyField label="How they’re paid" wide>
          <select className={inputClass} value={form.route} onChange={set('route')}>
            {Object.entries(data.giftRoutes!).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </MoneyField>
        <MoneyField label="How many">
          <input className={inputClass} value={form.count} onChange={set('count')} inputMode="numeric" />
        </MoneyField>
        <MoneyField label="Value per card">
          <input
            className={inputClass}
            value={form.value}
            onChange={set('value')}
            inputMode="decimal"
            placeholder="$"
          />
        </MoneyField>
        <MoneyField label="Vendor">
          <input
            className={inputClass}
            value={form.vendor}
            onChange={set('vendor')}
            placeholder="For example, Starbucks"
          />
        </MoneyField>
        <MoneyField label="What for">
          <input
            className={inputClass}
            value={form.use}
            onChange={set('use')}
            placeholder="For example, PheedLoop game prizes"
          />
        </MoneyField>
        <MoneyField label="Who receives them" wide>
          <input
            className={inputClass}
            value={form.recipients}
            onChange={set('recipients')}
            placeholder="For example, top 10 players; Elder"
          />
        </MoneyField>
      </div>
    </Modal>
  );
}

/** A checklist of steps: tick to record, untick to undo; done steps show their date. */
export function StepChecklist({
  steps,
  disabled,
  onToggle,
  extra,
}: {
  steps: Step[];
  disabled: boolean;
  onToggle: (s: Step) => void;
  extra?: (s: Step) => ReactNode;
}) {
  return (
    <ul className="text-[15px] leading-[22px]">
      {steps.map((st) => (
        <li key={st.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 min-h-[40px] border-b border-line">
          <label className="inline-flex items-center gap-3 flex-[1_1_0] min-w-[200px] py-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-[18px] h-[18px] accent-accent flex-none"
              checked={st.done}
              disabled={disabled}
              onChange={() => onToggle(st)}
            />
            <span className={st.done ? 'text-ink' : 'text-ink-2'}>{st.label}</span>
          </label>
          {extra?.(st)}
          {st.done ? <span className="text-small text-ink-3 tabular">{dateText(st.at)}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** Vendors or gift cards not begun yet: one row each, what it is for and how to start. */
export function NotStarted({
  rows,
}: {
  rows: { what: string; why: string; action: string | null; onClick: () => void }[];
}) {
  return (
    <Panel>
      <PanelHeader title="Not started yet" />
      <PanelBody>
        <table className={`${stack.table} md:table-fixed`}>
          <colgroup>
            <col className="w-[22%]" />
            <col />
            <col className="w-[220px]" />
          </colgroup>
          <thead className={stack.thead}>
            <tr className={headRow}>
              <th className={th}>What</th>
              <th className={th}>Why start it</th>
              <th className={th}>
                <span className="sr-only">Action</span>
              </th>
            </tr>
          </thead>
          <tbody className={stack.tbody}>
            {rows.map((r) => (
              <tr key={r.what} className={stack.tr}>
                <td className={`${td} ${stack.td} font-semibold`}>{r.what}</td>
                <td className={`${td} ${stack.td} text-ink-2`}>{r.why}</td>
                <td className={`${td} ${stack.td} ${stack.acts}`}>
                  {r.action ? (
                    <Button size="sm" onClick={r.onClick}>
                      {r.action}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </PanelBody>
    </Panel>
  );
}

export const GIFT_CARDS_WHY =
  'Procurement approves a PCard purchase first; a participant fee or an honorarium are the other routes.';

function RequestBlock({ r, eventId, canEdit }: { r: GiftRequest; eventId: string; canEdit: boolean }) {
  const refresh = useMoneyRefresh(eventId);
  const step = useMutation(
    async ({ key, undo }: { key: string; undo: boolean }) =>
      api.patch(`/events/money/gift-cards/${r.id}`, { step: key, undo }),
    { onSuccess: refresh },
  );
  const cancel = useMutation(async () => api.patch(`/events/money/gift-cards/${r.id}`, { cancelled: !r.cancelledAt }), {
    onSuccess: refresh,
  });
  const email = [
    `Count: ${r.count}`,
    `Value per card: ${money(r.valueCents)}`,
    `Total: ${money(r.totalCents)}`,
    `Vendor: ${r.vendor ?? ''}`,
    `Use: ${r.use ?? ''}`,
    `Intended recipients: ${r.recipients ?? ''}`,
  ].join('\n');
  return (
    <article className="py-6 border-t border-line first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="min-w-0">
          <h3 className="text-[17px] leading-6 font-semibold text-ink tabular">
            {r.count} × {money(r.valueCents)} = {money(r.totalCents)}
            {r.vendor ? <span className="font-normal"> · {r.vendor}</span> : null}
          </h3>
          <p className="text-small text-ink-3">{r.routeLabel}</p>
        </div>
        <div className="flex items-center gap-3">
          {r.cancelledAt ? <Pill tone="inactive">Cancelled</Pill> : null}
          {canEdit ? (
            <MoreMenu
              label={`More for the ${r.count} gift cards`}
              items={[
                {
                  label: r.cancelledAt ? 'Restore this request' : 'Cancel this request',
                  destructive: !r.cancelledAt,
                  disabled: cancel.isLoading,
                  onClick: () => cancel.mutate(),
                },
              ]}
            />
          ) : null}
        </div>
      </div>
      {r.use || r.recipients ? (
        <p className="text-[15px] leading-[22px] text-ink-2 mt-2">
          {[r.use, r.recipients ? `for ${r.recipients}` : ''].filter(Boolean).join(', ')}
        </p>
      ) : null}
      <div className="mt-3 max-w-[720px]">
        <StepChecklist
          steps={r.steps}
          disabled={!canEdit || step.isLoading || Boolean(r.cancelledAt)}
          onToggle={(st) => step.mutate({ key: st.key, undo: st.done })}
        />
      </div>
      <p className="text-small text-ink-3 mt-2">
        {r.recordedInLedger} gift-card entr{r.recordedInLedger === 1 ? 'y' : 'ies'} for this event in the ledger (record
        winners on the Attendance tab).
      </p>
      {r.route === 'pcard' ? (
        <details className="text-[15px] leading-[22px] mt-2">
          <summary className="cursor-pointer w-fit text-accent hover:text-ink list-none [&::-webkit-details-marker]:hidden">
            Text for the approval email to Procurement (PCard)
          </summary>
          <pre className="whitespace-pre-wrap font-sans bg-surface border border-line rounded-sm p-3 mt-2 text-ink">
            {email}
          </pre>
        </details>
      ) : null}
      {step.error || cancel.error ? (
        <div className="mt-2">
          <ErrorNotice>{errorMessage(step.error || cancel.error, 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
    </article>
  );
}

/** Gift cards: Procurement pre-approval, purchase and reconciliation; or a participant fee or honorarium. */
export default function GiftCardRequestsPanel({ eventId, canEdit }: SimplePanelProps) {
  const q = useEventMoney(eventId);
  useMarkPresent(giftCardsKey(eventId));
  const vendorsHere = usePresent(vendorsKey(eventId));
  const leadHere = usePresent(budgetLeadKey(eventId));
  const [adding, setAdding] = useState(false);
  if (q.isLoading) return <SkeletonRows rows={3} />;
  if (q.error || !q.data)
    return <ErrorNotice>{errorMessage(q.error, 'Could not load the gift-card requests.')}</ErrorNotice>;
  const d = q.data;
  if (!d.visible)
    return leadHere ? null : (
      <Panel>
        <PanelHeader title="Gift cards" />
        <PanelBody>
          <CountsOnly data={d} />
        </PanelBody>
      </Panel>
    );
  const list = d.giftCardRequests ?? [];
  const dialog = adding ? <GiftCardDialog eventId={eventId} data={d} onDone={() => setAdding(false)} /> : null;
  if (!list.length) {
    // With no vendors either, the vendors section draws one "Not started yet" table holding both rows.
    if (vendorsHere && !(d.vendors ?? []).length) return null;
    return (
      <>
        <NotStarted
          rows={[
            {
              what: 'Gift cards',
              why: GIFT_CARDS_WHY,
              action: canEdit ? 'Request gift cards' : null,
              onClick: () => setAdding(true),
            },
          ]}
        />
        {dialog}
      </>
    );
  }
  const live = list.filter((r) => !r.cancelledAt).length;
  return (
    <Panel>
      <PanelHeader title="Gift cards" note={`${live} request${live === 1 ? '' : 's'}`}>
        {canEdit ? <Button onClick={() => setAdding(true)}>Request gift cards</Button> : null}
      </PanelHeader>
      <PanelBody>
        {list.map((r) => (
          <RequestBlock key={r.id} r={r} eventId={eventId} canEdit={canEdit} />
        ))}
      </PanelBody>
      {dialog}
    </Panel>
  );
}
